/* eslint-disable */
import { useEffect, useMemo, useRef, useState } from 'react';
import FormRow from '../../../components/formRow';
import { FormFieldGeneric } from '../../../components/formField';
import { OrangeButton } from '../../../components/buttonsComponent';
import * as promptService from '../../../services/prompt';
import { warningNotification, errorNotification } from '../../../components/notification';
import './style.css';

const chipStyles = (status) => {
  const map = {
    loading: { bg: '#fff7e6', bd: '#ffd591', fg: '#ad6800', text: 'enviando…' },
    ok: { bg: '#eef9f1', bd: '#b7eb8f', fg: '#135200', text: 'ok' },
    error: { bg: '#ffecec', bd: '#ffa39e', fg: '#a8071a', text: 'erro' },
  };
  const s = map[status] || map.ok;
  return {
    chip: {
      fontSize: 12,
      padding: '2px 8px',
      borderRadius: 999,
      background: s.bg,
      border: `1px solid ${s.bd}`,
      color: s.fg,
    },
    text: s.text,
  };
};

const Card = ({ title, subtitle, status, children, tone = 'neutral' }) => {
  const tones = {
    question: { bg: '#ffffff', bd: '#f0f0f0' },
    answer: { bg: '#fafcff', bd: '#e9f0ff' },
    neutral: { bg: '#ffffff', bd: '#f0f0f0' },
  };
  const t = tones[tone] || tones.neutral;
  const chip = chipStyles(status);

  return (
    <div
      style={{
        background: t.bg,
        border: `1px solid ${t.bd}`,
        borderRadius: 12,
        padding: 12,
        display: 'grid',
        gap: 8,
        boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <strong>{title}</strong>
        {!!status && <span style={chip.chip}>{chip.text}</span>}
        <span style={{ marginLeft: 'auto', fontSize: 12, opacity: 0.6 }}>{subtitle}</span>
      </div>
      <div>{children}</div>
    </div>
  );
};

const PromptHistory = () => {
  const [prompt, setPrompt] = useState('');
  const [threadId, setThreadId] = useState(null);
  const [history, setHistory] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const listRef = useRef(null);

  const hasItems = history.length > 0;
  const newId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [history]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = (prompt ?? '').toString().trim();
    if (!trimmed) {
      warningNotification('Digite uma pergunta antes de enviar.');
      return;
    }

    const id = newId();
    let createdAt = new Date();

    setHistory((prev) => [
      ...prev,
      { id, question: trimmed, answer: '', status: 'loading', createdAt },
    ]);
    setPrompt('');
    setIsSubmitting(true);

    try {
      const body = { Prompt: trimmed, ThreadId: threadId, History: true }
      const response = await promptService.openAI(body);
      createdAt = new Date();
      if (response?.status === 200) {
        const { data } = response;
        setThreadId(data.ThreadId);
        setHistory((prev) =>
          prev.map((item) => (item.id === id ? { ...item, answer: data.Answer, status: 'ok', end: createdAt.toLocaleString() } : item))
        );
      } else {
        const { data } = response || {};
        if (data?.MensagemUsuario !== undefined) warningNotification(data.MensagemUsuario);
        else errorNotification('Houve um erro ao tentar consultar a IA.');

        setHistory((prev) =>
          prev.map((item) =>
            item.id === id
              ? { ...item, answer: data?.MensagemUsuario || '—', status: 'error', end: createdAt.toLocaleString() }
              : item
          )
        );
      }
    } catch (err) {
      console.error(err);
      errorNotification('Falha de comunicação com o serviço de IA.');
      setHistory((prev) =>
        prev.map((item) =>
          item.id === id ? { ...item, answer: 'Erro ao consultar a IA.', status: 'error', end: createdAt.toLocaleString() } : item
        )
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClear = () => {
    if (!hasItems) return;
    setHistory([]);
    setThreadId(null);
  };

  const formattedItems = useMemo(
    () =>
      history.map((h) => ({
        ...h,
        when: new Date(h.createdAt).toLocaleString(),
      })),
    [history]
  );

  return (
    <div id="prompt-history">
      <h1>Prompt com Histórico - Inteligência Artificial</h1>
      <div
        ref={listRef}
        style={{
          marginTop: 12,
          maxHeight: '70vh',
          overflowY: 'auto',
          border: '1px solid #eee',
          borderRadius: 12,
          padding: 12,
          background: '#fafafa',
        }}
      >
        {!hasItems && (
          <div style={{ opacity: 0.7 }}>
            Nenhuma conversa ainda. Faça a sua primeira pergunta.
          </div>
        )}

        {formattedItems.map((item) => (
          <div
            key={item.id}
            style={{
              display: 'grid',
              gap: 10,
              paddingBottom: 14,
              marginBottom: 14,
            }}
          >
            {/* Card: Pergunta (direita) */}
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Card
                title=""
                subtitle={item.when}
                tone="question"
              >
                <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{item.question}</p>
              </Card>
            </div>

            {/* Card: Resposta (esquerda) */}
            <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
              <Card title="Resposta" subtitle={item.status === 'loading' ? '' : item.end} status={item.status} tone="answer">
                <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                  {item.status === 'loading' ? 'Aguardando resposta…' : item.answer}
                </pre>
              </Card>
            </div>
          </div>
        ))}
      </div>
      <form onSubmit={handleSubmit}>
        <FormRow withShrink>
          <FormFieldGeneric
            classNameWrapper="fill-50-field"
            titleLabel="Digite sua pergunta..."
            valueInput={prompt}
            onChangeValue={(e) => setPrompt(e.value)}
          />

          <div className="side-button" style={{ display: 'flex', gap: 8 }}>
            <OrangeButton type="submit" name="enviar" disabled={isSubmitting}>
              {isSubmitting ? 'Enviando…' : 'Enviar'}
            </OrangeButton>
            <OrangeButton type="button" name="limpar" onClick={handleClear} disabled={!hasItems}>
              Limpar histórico
            </OrangeButton>
          </div>
        </FormRow>
      </form>
    </div>
  );
};

export default PromptHistory;
